import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Check, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const money = (n) => "$" + (Number(n) || 0).toLocaleString();
const badge = { "Paid": "bg-success/15 text-success border-success/30", "Overdue": "", "Pending": "bg-warning/15 text-warning border-warning/30" };

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", amount: "", due_date: "", description: "", status: "Pending" });

  const load = () => api.get("/invoices").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); api.get("/clients?page_size=500").then((r) => setClients(r.data.items)).catch(() => {}); }, []);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("zeefa:refresh", h);
    return () => window.removeEventListener("zeefa:refresh", h);
  }, []);

  const create = async () => {
    if (!form.amount) { toast.error("Amount required"); return; }
    const client = clients.find((c) => c.id === form.client_id);
    await api.post("/invoices", { ...form, amount: parseFloat(form.amount), client_name: client?.name || "" });
    setOpen(false); setForm({ client_id: "", amount: "", due_date: "", description: "", status: "Pending" });
    load(); toast.success("Invoice created");
  };

  const markPaid = async (id) => { await api.put(`/invoices/${id}`, { status: "Paid" }); load(); toast.success("Marked paid"); };

  const remove = async (i) => {
    if (!window.confirm(`Delete invoice ${i.invoice_no}? This cannot be undone.`)) return;
    try { await api.delete(`/invoices/${i.id}`); load(); toast.success("Invoice deleted"); }
    catch (e) { toast.error("Could not delete invoice"); }
  };

  const totals = {
    pending: rows.filter((r) => r.status !== "Paid").reduce((a, b) => a + (Number(b.amount) || 0), 0),
    paid: rows.filter((r) => r.status === "Paid").reduce((a, b) => a + (Number(b.amount) || 0), 0),
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Invoices</h1>
          <p className="text-muted-foreground mt-1">Outstanding <span data-testid="invoices-outstanding-total" className="font-mono text-foreground">{money(totals.pending)}</span> · Collected <span data-testid="invoices-collected-total" className="font-mono text-foreground">{money(totals.paid)}</span></p>
        </div>
        <Button className="rounded-sm gap-2" onClick={() => setOpen(true)} data-testid="new-invoice-btn"><Plus size={16} /> New Invoice</Button>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Invoice #</TableHead><TableHead>Client</TableHead><TableHead>Description</TableHead>
              <TableHead>Due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="invoices-table-body">
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No invoices yet.</TableCell></TableRow>}
            {rows.map((i) => (
              <TableRow key={i.id} data-testid={`invoice-row-${i.id}`}>
                <TableCell className="font-mono text-sm">{i.invoice_no}</TableCell>
                <TableCell className="font-medium">{i.client_name || "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{i.description || "—"}</TableCell>
                <TableCell className="text-sm">{i.due_date || "—"}</TableCell>
                <TableCell><Badge variant={i.status === "Overdue" ? "destructive" : "outline"} className={`rounded-sm ${badge[i.status] || ""}`}>{i.status}</Badge></TableCell>
                <TableCell className="text-right font-mono font-semibold">{money(i.amount)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-3">
                    {i.status !== "Paid" && <button onClick={() => markPaid(i.id)} data-testid={`mark-paid-${i.id}`} className="text-xs text-success flex items-center gap-1 hover:underline"><Check size={13} /> Mark paid</button>}
                    <button onClick={() => remove(i)} data-testid={`delete-invoice-${i.id}`} title="Delete invoice" className="text-muted-foreground hover:text-red-600 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">New Invoice</DialogTitle><DialogDescription>Create an invoice and assign it to a client.</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label>Client</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="inv-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Amount</Label><Input data-testid="inv-amount-input" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm font-mono" /></div>
              <div className="grid gap-1.5"><Label>Due date</Label><Input data-testid="inv-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm" /></div>
            </div>
            <div className="grid gap-1.5"><Label>Description</Label><Input data-testid="inv-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
            <div className="grid gap-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="rounded-sm" data-testid="inv-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Pending">Pending</SelectItem><SelectItem value="Overdue">Overdue</SelectItem><SelectItem value="Paid">Paid</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={create} data-testid="invoice-create-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
